import { useEffect, useState } from 'react';

import { AuthenticationStore, useAuthentication } from '@freelancelabs/components';

interface Gate {
    /** The portal must not open: the requirement stands and no factor is registered. */
    blocked: boolean;
    /** The pool has not answered yet. Nothing is decided, so nothing is rendered. */
    checking: boolean;
}

/**
 * Whether the portal must stop somebody before letting them in, because one of their
 * accounts requires MFA and they carry no factor.
 *
 * Two facts, two sources: the requirement comes from our API (`viewer.mfaRequired`), a
 * registered factor is a fact of the Cognito pool. It fails open, so a transient read
 * error (or a false `required`) defers the demand rather than locking anyone out.
 */
export const useMfaGate = (required: boolean | null | undefined): Gate => {
    const { fetchMfaSettings, isFederatedSession } = useAuthentication() as AuthenticationStore;
    const [state, setState] = useState<Gate>({ blocked: false, checking: false });

    useEffect(() => {
        if (required !== true) {
            // Nothing to ask the pool: no requirement, or the query has not answered.
            setState({ blocked: false, checking: false });
            return;
        }

        let live = true;
        setState({ blocked: false, checking: true });

        // No MFA for a federated (SSO) session: Entra ID already asked for the second
        // factor on this path. It is a fact of the session, not the person.
        isFederatedSession()
            .then(async (federated) => {
                // Don't fabricate a pool answer: a complaisant `totp: true` would claim a
                // factor the pool never reported.
                if (federated) {
                    return false;
                }
                const { totp } = await fetchMfaSettings();
                return !totp;
            })
            .then((blocked) => {
                if (live) {
                    setState({ blocked, checking: false });
                }
            })
            .catch(() => {
                if (live) {
                    setState({ blocked: false, checking: false });
                }
            });

        // A late resolution must not put the barrier back up on a screen that has moved on.
        return () => {
            live = false;
        };
    }, [required]);

    return state;
};

export default useMfaGate;
